import Chart from 'chart.js/auto';
import { api } from '../api.js';
import { canDo } from '../auth.js';
import { isOwner, tendersSummary } from '../utils/tenders.js';
import { colorForProject } from '../utils/colors.js';
import { feverEmoji, formatMinutes, formatPercent } from '../utils/format.js';

const WEEKDAY_RU = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
const MONTH_RU = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

let _chart = null;

export async function initDashboard(container) {
  // Tear down previous chart instance if any (router re-init).
  if (_chart) { try { _chart.destroy(); } catch { /* ignore */ } _chart = null; }

  container.innerHTML = '<div class="loading-spinner">Загрузка дашборда...</div>';

  const today = new Date();
  const dow = today.getDay();
  const dateStr = `${today.getDate()} ${MONTH_RU[today.getMonth()]}`;
  const start = today.toISOString().slice(0, 10);
  const end = new Date(today.getTime() + 28 * 86400_000).toISOString().slice(0, 10);

  const settle = (p) => p.then(v => ({ ok: true, v })).catch(e => ({ ok: false, e }));

  const [priority, fever, capacity, workload, recurring, calibration] = await Promise.all([
    settle(api.stats.todayPriority({ limit: 5 })),
    settle(api.stats.feverChart()),
    settle(api.stats.weeklyCapacity()),
    settle(api.resourceWorkload(1, start, end)),
    settle(api.recurringBlocks()),
    settle(api.stats.calibration(90)),
  ]);

  // Header capacity for today (day-of-week match)
  let todayCapacity = '—';
  if (capacity.ok) {
    const todayRow = (capacity.v.daily ?? []).find(r => Number(r.day_of_week) === dow);
    const m = Number(todayRow?.net_project_minutes ?? 0);
    todayCapacity = `${m} мин (${(m / 60).toFixed(1)} ч)`;
  }

  container.innerHTML = `
    <div class="dashboard-root">
      <div class="dashboard-header">
        Сегодня: <strong>${WEEKDAY_RU[dow]}, ${dateStr}</strong>. Ёмкость: <strong>${todayCapacity}</strong>
      </div>
      ${canDo('tender.read') ? `
      <section class="dash-tenders" id="dash-tenders">
        <span class="dash-tenders-title">Аукционы:</span>
        <a class="dash-tenders-link" data-route="/tenders?status=decision">ждут решения <strong data-dash-tenders="decision">…</strong></a>
        <a class="dash-tenders-link" data-route="/tenders?deadline=2">дедлайн подачи ≤ 2 дней <strong data-dash-tenders="soon">…</strong></a>
        ${isOwner() ? '<a class="dash-tenders-link" data-route="/tenders?prefilter=review">на проверку у Руслана: <strong data-dash-tenders="review">…</strong></a>' : ''}
        ${isOwner() ? '<span class="dash-tenders-link" data-dash-guess title="Доля решений «согласен с рекомендацией» (участие с отклонением цены до 10 %)">Раскладка угадывает: <strong data-dash-tenders="guess">…</strong></span>' : ''}
      </section>` : ''}

      <section class="dash-widget" id="dash-priority">
        <header class="dash-widget-header" data-route="/gantt">
          <h3>🎯 Приоритетные задачи</h3>
          <a class="dash-widget-link" data-route="/gantt">→ все задачи</a>
        </header>
        <div class="dash-widget-body" id="dash-priority-body">
          ${renderPriority(priority)}
        </div>
      </section>

      <section class="dash-widget" id="dash-fever">
        <header class="dash-widget-header" data-route="/fever">
          <h3>🔥 Fever Chart</h3>
          <a class="dash-widget-link" data-route="/fever">→ детальная диаграмма</a>
        </header>
        <div class="dash-widget-body">
          ${renderFever(fever)}
        </div>
      </section>

      <section class="dash-widget dash-histogram" id="dash-histogram">
        <header class="dash-widget-header" data-route="/resources">
          <h3>📊 Загрузка тебя — следующие 4 недели</h3>
          <a class="dash-widget-link" data-route="/resources">→ Resources</a>
        </header>
        <div class="dash-widget-body" style="position: relative;">
          <canvas id="dash-histogram-canvas" height="200"></canvas>
          <div id="dash-overload" class="dash-overload-warn"></div>
        </div>
      </section>

      <section class="dash-widget dash-schedule">
        <header class="dash-widget-header">
          <h3>🔄 Сегодня в расписании</h3>
        </header>
        <div class="dash-widget-body">
          ${renderSchedule(recurring, dow)}
        </div>
      </section>

      <section class="dash-widget" id="dash-calibration">
        <header class="dash-widget-header" data-route="/calibration">
          <h3>🎯 Точность оценок (90 дней)</h3>
          <a class="dash-widget-link" data-route="/calibration">→ детальная аналитика</a>
        </header>
        <div class="dash-widget-body">
          ${renderCalibration(calibration)}
        </div>
      </section>
    </div>
  `;

  if (workload.ok) renderHistogram(workload.v);
  if (canDo('tender.read')) fillTendersWidget(container);
  if (canDo('tender.read') && isOwner()) fillGuessWidget(container);
}

// ── М-1: «Раскладка угадывает: N% за 30 дней» (owner) ────────────────────────

export function guessText(s) {
  if (!s || !s.total) return 'нет решений за 30 дней';
  const pct = Math.round(Number(s.agree_pct ?? 0));
  const dev = s.avg_deviation_pct == null ? ''
    : `, поправка цены ${s.avg_deviation_pct > 0 ? '+' : ''}${Math.round(s.avg_deviation_pct)} %`;
  return `${pct}% за ${s.days} дней (${s.agree} из ${s.total}${dev})`;
}

async function fillGuessWidget(container) {
  const el = container.querySelector('[data-dash-tenders="guess"]');
  if (!el) return;
  try {
    const s = await api.tenders.decisionStats(30);
    el.textContent = guessText(s);
    const top = (s.top_categories ?? []).map(c => `${c.label}: ${c.n}`).join('; ');
    if (top) el.closest('[data-dash-guess]').title = `Топ причин за ${s.days} дней — ${top}`;
  } catch {
    el.textContent = '—';
  }
}

// ── Widget: Аукционы (A3) — ссылки ведут в /tenders с фильтром ───────────────

async function fillTendersWidget(container) {
  const set = (key, v) => {
    const el = container.querySelector(`[data-dash-tenders="${key}"]`);
    if (el) el.textContent = v;
  };
  try {
    const s = await tendersSummary();
    set('decision', String(s.decision));
    set('soon', String(s.soon));
    set('review', String(s.review));
  } catch {
    set('decision', '—');
    set('soon', '—');
    set('review', '—');
  }
}

// ── Widget 1: Priority tasks ─────────────────────────────────────────────────

function renderPriority(res) {
  if (!res.ok) return `<div class="dash-error">Ошибка: ${escHtml(res.e.message)}</div>`;
  const tasks = res.v;
  if (!tasks.length) return '<div class="dash-error">Нет задач на сегодня</div>';
  return tasks.map(t => `
    <div class="dash-priority-item" data-route="/gantt/${t.project_id}">
      <span>${feverEmoji(t.fever_zone)}</span>
      <div style="flex: 1; min-width: 0;">
        <div class="dash-priority-name" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          ${t.is_on_critical_chain ? '<span style="color: var(--critical-chain); font-size: var(--text-xs); margin-right: 4px;">CC</span>' : ''}
          ${escHtml(t.task_name)}
        </div>
        <div class="dash-priority-meta">${escHtml(t.project_name)}</div>
      </div>
      <span class="dash-priority-meta">${formatMinutes(t.ruslan_minutes)}</span>
    </div>
  `).join('');
}

// ── Widget 2: Fever chart preview ────────────────────────────────────────────

function renderFever(res) {
  if (!res.ok) return `<div class="dash-error">Ошибка: ${escHtml(res.e.message)}</div>`;
  const projects = res.v;
  if (!projects.length) return '<div class="dash-error">Нет активных проектов</div>';
  return projects.map(p => `
    <div class="dash-fever-chip" data-route="/gantt/${p.project_id}">
      <span>${feverEmoji(p.fever_zone)} ${escHtml(p.project_name)}</span>
      <span style="color: var(--text-secondary);">
        прогресс ${formatPercent(p.progress_pct)} · буфер ${formatPercent(p.buffer_consumed_pct)}
      </span>
    </div>
  `).join('');
}

// ── Widget 3: Mini resource histogram ────────────────────────────────────────

function renderHistogram(rows) {
  const canvas = document.getElementById('dash-histogram-canvas');
  if (!canvas) return;

  const labels = rows.map(r => {
    const d = new Date(r.work_date);
    return `${d.getDate()}.${(d.getMonth() + 1).toString().padStart(2, '0')}`;
  });

  // Stack per-project bars from project_breakdown JSONB.
  const projectMap = new Map();   // pid → { label, data[] }
  rows.forEach((r, i) => {
    const breakdown = r.project_breakdown ?? [];
    for (const p of breakdown) {
      const pid = p.project_id ?? p.id ?? 0;
      if (!projectMap.has(pid)) {
        projectMap.set(pid, {
          label: p.project_name ?? `Проект ${pid}`,
          data: Array(rows.length).fill(0),
          backgroundColor: colorForProject(pid),
          stack: 'load',
        });
      }
      projectMap.get(pid).data[i] += Number(p.daily_minutes ?? p.minutes ?? 0);
    }
  });

  const datasets = Array.from(projectMap.values());
  const capacity = rows.map(r => Number(r.capacity_minutes ?? 0));
  datasets.push({
    type: 'line',
    label: 'Ёмкость',
    data: capacity,
    borderColor: 'rgba(45, 212, 191, 0.9)',  // teal-bright (Roxber)
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    pointRadius: 0,
    stepped: true,
  });

  if (_chart) { _chart.destroy(); _chart = null; }
  _chart = new Chart(canvas.getContext('2d'), {
    type: 'bar',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { stacked: true, ticks: { color: '#8b949e', font: { size: 9 } } },
        y: { stacked: true, ticks: { color: '#8b949e', font: { size: 10 } } },
      },
    },
  });

  // Overload summary
  const overloads = rows.filter(r => Number(r.overload_minutes ?? 0) > 0);
  const el = document.getElementById('dash-overload');
  if (overloads.length && el) {
    const days = overloads.slice(0, 3).map(r => {
      const d = new Date(r.work_date);
      const pct = Math.round(Number(r.overload_minutes) / Math.max(1, Number(r.capacity_minutes)) * 100);
      return `${d.getDate()}.${(d.getMonth() + 1).toString().padStart(2, '0')} (+${pct}%)`;
    }).join(', ');
    el.innerHTML = `⚠️ Дни перегруза: ${days}${overloads.length > 3 ? ' и ещё ' + (overloads.length - 3) : ''}`;
  }
}

// ── Widget 4: Today schedule ─────────────────────────────────────────────────

function renderSchedule(res, dow) {
  if (!res.ok) return `<div class="dash-error">Ошибка: ${escHtml(res.e.message)}</div>`;
  const blocks = (res.v.blocks ?? []).filter(b => Array.isArray(b.days_of_week) && b.days_of_week.includes(dow));
  if (!blocks.length) return '<div class="dash-error">Сегодня без блоков</div>';
  blocks.sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
  return `<div class="dash-schedule-row">${blocks.map(b => `
    <span class="dash-schedule-block">
      <strong>${String(b.start_time).slice(0, 5)}</strong>${escHtml(b.name)}
    </span>
  `).join('')}</div>`;
}

// ── Widget 5: Calibration accuracy ───────────────────────────────────────────

function renderCalibration(res) {
  if (!res.ok) return `<div class="dash-error">Ошибка: ${escHtml(res.e.message)}</div>`;
  const d = res.v;
  const N = Number(d.sample_size ?? 0);
  if (N < 5) {
    const left = Math.max(5 - N, 0);
    return `
      <div class="dash-cal-pending">
        <div class="dash-cal-pending-text">
          Выборка пока маленькая. Завершено: <strong>${N}</strong> из 5 минимально нужных задач.
        </div>
        <div class="dash-cal-pending-hint">
          Калибрация наполнится автоматически по мере того, как задачи закрываются с фактом.
          Накопить ещё ${left} задач${left === 1 ? 'у' : left < 5 ? 'и' : ''}.
        </div>
      </div>
    `;
  }
  const ratio = Number(d.avg_ratio_pct ?? 0);
  const sd    = d.stddev_pct != null ? Number(d.stddev_pct) : null;
  const note  = N < 20 ? '<span class="dash-cal-note">⚠ выборка пока маленькая</span>' : '';
  return `
    <div class="dash-cal-grid">
      <div class="dash-cal-main">
        <div class="dash-cal-main-value">${ratio.toFixed(0)}%</div>
        <div class="dash-cal-main-key">средний коэффициент</div>
      </div>
      <div class="dash-cal-side">
        <div class="dash-cal-row"><span>Завершено задач:</span> <strong>${N}</strong></div>
        ${sd != null ? `<div class="dash-cal-row"><span>Разброс (σ):</span> <strong>±${sd.toFixed(0)}%</strong></div>` : ''}
        <div class="dash-cal-row"><span>Min / Max:</span> <strong>${(d.min_ratio_pct ?? '—')}% / ${(d.max_ratio_pct ?? '—')}%</strong></div>
        ${note}
      </div>
    </div>
  `;
}

function escHtml(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
